import { layOutOptions, mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * The fourteen levels.
 *
 * Written out one by one rather than computed from difficulty, because the sequence IS the
 * curriculum and a formula would hide it. Anyone changing the order of teaching should have to
 * edit a list that reads like an order of teaching.
 */
function recipeFor(difficulty) {
    const base = { flashMs: 0, optionCount: 3 };
    switch (difficulty) {
        /**
         * Counting to four, not to three.
         *
         * Three was the original ceiling and it made the first level of the first game monotonous: three
         * possible questions across a twelve-trial sitting means every value appears four times, and the
         * opening ran 2, 2, 2. Four is still inside what a four-year-old can subitise and it is a third more
         * of everything. What actually fixed the repetition is `countFor` below; this only widens the pool.
         */
        case 1:
            return { ...base, operation: "count", notation: "objects", answerNotation: "objects", ceiling: 4 };
        case 2:
            return { ...base, operation: "count", notation: "objects", answerNotation: "digit", ceiling: 6 };
        // The one flashed level: the dots vanish, so they cannot be counted one at a time.
        case 3:
            return { ...base, operation: "count", notation: "objects", answerNotation: "digit", ceiling: 5, flashMs: 1200 };
        /**
         * Counting to ten, then to twenty.
         *
         * **The ladder used to stop counting at five and go straight to addition**, which is a year short
         * of where preschool counting actually ends — children learn to twenty, and the last stretch is the
         * one that takes longest. Adding two levels here is also what makes the arithmetic above them
         * honest: adding to ten is a different task for a child who can count to ten than for one who
         * cannot.
         *
         * Above six the objects are laid out in rows of five, which is how counting past ten is taught and
         * the only arrangement in which twenty dots can be read rather than tallied.
         */
        case 4:
            return { ...base, operation: "count", notation: "objects", answerNotation: "digit", ceiling: 10, optionCount: 4 };
        case 5:
            return { ...base, operation: "count", notation: "objects", answerNotation: "digit", ceiling: 20, optionCount: 4 };
        case 6:
            return { ...base, operation: "add", notation: "objects", answerNotation: "digit", ceiling: 5, optionCount: 3 };
        case 7:
            return { ...base, operation: "add", notation: "digit", answerNotation: "digit", ceiling: 5, optionCount: 4 };
        case 8:
            return { ...base, operation: "add", notation: "objects", answerNotation: "digit", ceiling: 10, optionCount: 4 };
        case 9:
            return { ...base, operation: "add", notation: "digit", answerNotation: "digit", ceiling: 10, optionCount: 4 };
        case 10:
            return { ...base, operation: "subtract", notation: "objects", answerNotation: "digit", ceiling: 5, optionCount: 3 };
        case 11:
            return { ...base, operation: "subtract", notation: "digit", answerNotation: "digit", ceiling: 10, optionCount: 4 };
        /**
         * Mixed, and it is its own level for a reason.
         *
         * A child who has done twenty subtractions in a row stops reading the sign. Interleaving the
         * two is the first level that asks which operation this is, which is a different question
         * from how to do either.
         */
        case 12:
            return { ...base, operation: "add", notation: "digit", answerNotation: "digit", ceiling: 10, optionCount: 4 };
        case 13:
            return { ...base, operation: "add", notation: "digit", answerNotation: "digit", ceiling: 20, optionCount: 4 };
        case 14:
            return { ...base, operation: "multiply", notation: "objects", answerNotation: "digit", ceiling: 12, optionCount: 4 };
        case 15:
            return { ...base, operation: "multiply", notation: "digit", answerNotation: "digit", ceiling: 100, optionCount: 4 };
        default:
            return { ...base, operation: "divide", notation: "digit", answerNotation: "digit", ceiling: 100, optionCount: 4 };
    }
}
/**
 * The counting value for one trial: **one, then two, then three, in order, every time.**
 *
 * The same rule the alphabet game follows and for the same reason. A class is taught to count by
 * counting — ۱، ۲، ۳ — and a teacher who opens this game expects it to open on one. This used to be
 * a shuffled cycle: every value appeared once per block, which fixed a real complaint about runs of
 * 2, 2, 2, but it delivered them in an order nobody chose. Shuffling is the right answer to "which
 * wrong tiles are offered" and the wrong answer to "which number comes next", because in counting
 * the order IS the lesson.
 *
 * **The variety lives entirely in the cards below.** The form rotates every sitting and seeds both
 * the wrong answers and where they sit (`layOutOptions`), so the same quantity comes round with a
 * different row of tiles each time. The number is the lesson; the tiles are the practice.
 *
 * The cost is the one `letters.ts` writes down about the alphabet, and it is worth repeating here:
 * a sitting is twelve trials, so the level that counts to twenty covers one to twelve and no
 * further in one afternoon. Reaching the teens is the teacher's to drive through the level they
 * assign, not something a generator should arrange by starting a child in the middle.
 */
function countFor(trialIndex, ceiling) {
    return (trialIndex % ceiling) + 1;
}
export const MATH_MIN_DIFFICULTY = 1;
export const MATH_MAX_DIFFICULTY = 16;
/** The mixed level alternates the operation by trial rather than fixing it. */
const MIXED_LEVEL = 12;
export function generateMathProblem(task, formId, trialIndex, difficulty) {
    if (difficulty < MATH_MIN_DIFFICULTY || difficulty > MATH_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const recipe = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    // On the mixed level the operation alternates with the trial index rather than being drawn,
    // so a run of trials is guaranteed to contain both rather than merely likely to.
    const operation = difficulty === MIXED_LEVEL ? (trialIndex % 2 === 0 ? "add" : "subtract") : recipe.operation;
    /**
     * Counting walks; everything else gets a fair draw and one guard against repeating itself.
     *
     * The arithmetic levels have no natural order to walk — there is no first sum — and a much larger
     * answer space, so they are drawn. Two identical questions in a row still read as a bug, and
     * re-drawing once with a nudged seed is enough to make it rare without biasing anything a child
     * could exploit.
     */
    const { operands, answer } = operation === "count"
        ? (() => {
            const n = countFor(trialIndex, recipe.ceiling);
            return { operands: [n], answer: n };
        })()
        : drawUnrepeated(operation, recipe.ceiling, formId, trialIndex, difficulty);
    // Laid out rather than spliced into: where a tile sits must say nothing (`layOutOptions`).
    const { options, correctIndex } = layOutOptions(answer, buildOptions(answer, recipe, rand), rand);
    return {
        operation,
        operands,
        answer,
        notation: recipe.notation,
        /**
         * Objects for the answer only while the answer is small enough to count at a glance.
         *
         * Above five, a tile of dots is something a child has to count under time pressure, and
         * two tiles of seven and eight dots are indistinguishable at tile size. The tiles become
         * digits and the QUESTION stays in objects, which is the level's actual point.
         */
        answerNotation: answer <= 5 ? recipe.answerNotation : "digit",
        options,
        correctIndex,
        flashMs: recipe.flashMs,
    };
}
/**
 * A draw that is not identical to the previous trial's.
 *
 * One retry with a different seed, not a loop: at a ceiling of five there are few enough sums that
 * insisting on a change every time would distort which problems appear, and the point is only to stop
 * the same question appearing twice in a row.
 */
function drawUnrepeated(operation, ceiling, formId, trialIndex, difficulty) {
    const first = draw(operation, ceiling, mulberry32(seedFrom(formId, trialIndex, difficulty)));
    if (trialIndex === 0)
        return first;
    const previous = draw(operation, ceiling, mulberry32(seedFrom(formId, trialIndex - 1, difficulty)));
    const same = previous.answer === first.answer &&
        previous.operands.length === first.operands.length &&
        previous.operands.every((v, i) => v === first.operands[i]);
    if (!same)
        return first;
    return draw(operation, ceiling, mulberry32(seedFrom(`${formId}#again`, trialIndex, difficulty)));
}
/**
 * Operands that produce a legal answer.
 *
 * Every operation is drawn so the answer is a whole number within the ceiling and never
 * negative — the constraints are applied when drawing rather than by re-rolling until they hold,
 * because a re-roll loop over a narrow ceiling can spin and, worse, silently biases which
 * problems appear.
 */
function draw(operation, ceiling, rand) {
    const upto = (n) => 1 + Math.floor(rand() * n);
    switch (operation) {
        case "count": {
            const n = upto(ceiling);
            return { operands: [n], answer: n };
        }
        case "add": {
            // The first operand leaves room for a second, so the sum cannot exceed the ceiling.
            const a = upto(ceiling - 1);
            const b = upto(ceiling - a);
            return { operands: [a, b], answer: a + b };
        }
        case "subtract": {
            // Drawn as a total and a part taken from it, so the answer is never negative — a
            // five-year-old has no use for −2 and no way to read it.
            const total = 1 + Math.floor(rand() * ceiling);
            const taken = 1 + Math.floor(rand() * total);
            return { operands: [total, taken], answer: total - taken };
        }
        case "multiply": {
            // Small factors: the point is equal groups, not a times table. Bounded so the product
            // stays inside the ceiling.
            const limit = Math.max(2, Math.floor(Math.sqrt(ceiling)));
            const a = upto(limit);
            const b = upto(Math.max(1, Math.min(limit, Math.floor(ceiling / a))));
            return { operands: [a, b], answer: a * b };
        }
        case "divide": {
            // Built from the answer outwards, which is what guarantees it divides exactly. Deriving
            // the dividend from a drawn quotient is the only way to avoid remainders without
            // rejecting draws.
            const limit = Math.max(2, Math.floor(Math.sqrt(ceiling)));
            const quotient = upto(limit);
            const divisor = upto(limit);
            return { operands: [quotient * divisor, divisor], answer: quotient };
        }
    }
}
/**
 * Wrong answers that are wrong in the ways children are wrong.
 *
 * Not random numbers. The distractors are off-by-one (miscounted by one), the two operands
 * added when they should have been subtracted or the reverse, and one of the operands itself —
 * which is the most common error of all, a child who read the question and reported a number
 * from it. A random distractor set is solvable by elimination without doing the arithmetic.
 */
function buildOptions(answer, recipe, rand) {
    const out = [];
    const seen = new Set([answer]);
    /**
     * A little above the level's ceiling is allowed; below zero is not.
     *
     * The guard used to stop at the ceiling exactly, on the reasonable ground that a tile the child
     * can see is impossible narrows the choice for free. It cost more than it saved. On the levels
     * whose ceiling is five the answers bunch against it, so every offset upwards was rejected and
     * the answer came out the largest tile three times in four — "tap the biggest number" was worth
     * seventy-five percent on adding to five.
     *
     * Two above is not a giveaway. A child working out 3+2 has no way to know the answer cannot be
     * seven; knowing that IS the arithmetic. Below zero stays refused, because a negative tile is
     * not a wrong answer a five-year-old can even read, and on the object levels it cannot be drawn.
     */
    const offer = (value) => {
        if (out.length >= recipe.optionCount - 1)
            return;
        if (value < 0 || value > recipe.ceiling + 2)
            return;
        if (seen.has(value))
            return;
        seen.add(value);
        out.push(value);
    };
    /**
     * Off by a little, but not always in the same DIRECTION — and that is a bug fix, not a flourish.
     *
     * This used to offer `answer - 1` and then `answer + 1`, in that fixed order, on the reasonable
     * theory that a miscount goes one short more often than one over. The consequence was not
     * reasonable at all: with three tiles the wrong answers straddled the right one every single
     * time, so the answer was ALWAYS the middle number. Measured over the whole ladder, it was
     * never the smallest tile on seven levels out of eight, and on the division level it was the
     * middle of three a hundred percent of the time. "Tap the middle number" beat that level
     * outright, without dividing anything.
     *
     * Shuffling the tiles cannot fix it, because the leak is in the VALUES rather than in where
     * they sit — a child ranks the numerals they can see, not the slots. So the offsets are drawn
     * instead: sometimes both wrong answers sit below the answer, sometimes both above, sometimes
     * either side. Each one is still a miscount a child actually makes, which is the property that
     * stops the trial being solvable by elimination.
     *
     * **How many wrong answers sit BELOW the right one is drawn first, and it is drawn uniformly.**
     * That number is the answer's rank among the tiles, so drawing it is the only way to make the
     * rank flat — shuffling a symmetric pool of offsets is not enough, because with three tiles two
     * offsets drawn from three below and three above straddle the answer sixty percent of the time
     * by simple counting. Measured at exactly sixty, which is how this version came about.
     *
     * Near a boundary the realised rank still skews and that is honest rather than fixable: if the
     * answer is zero there is no wrong answer below it, so on the small subtraction levels the answer
     * really is the smallest tile more often than not. A generator cannot fix arithmetic.
     */
    const wanted = recipe.optionCount - 1;
    const below = Math.floor(rand() * (wanted + 1));
    for (const offset of shuffle([-1, -2, -3], rand).slice(0, below))
        offer(answer + offset);
    for (const offset of shuffle([1, 2, 3], rand).slice(0, wanted - below))
        offer(answer + offset);
    // Whatever a boundary refused, from either side.
    for (const offset of shuffle([-1, 1, -2, 2, -3, 3], rand))
        offer(answer + offset);
    // Then fill from further out, still a plausible cluster rather than scattering across the range.
    let step = 4;
    let guard = 0;
    while (out.length < recipe.optionCount - 1 && guard++ < 60) {
        offer(rand() < 0.5 ? answer + step : answer - step);
        step++;
    }
    // A tiny ceiling (three, at level one) can genuinely run out of distinct wrong answers.
    // Falling back to anything in range is right: the alternative is throwing on a legal level.
    for (let v = 0; v <= recipe.ceiling && out.length < recipe.optionCount - 1; v++)
        offer(v);
    return out;
}
/** The chosen tile must be the answer. */
export function scoreMathTrial(correctIndex, chosenIndex) {
    return correctIndex === chosenIndex;
}
//# sourceMappingURL=math.js.map